import { noop } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from 'i18n';

import { PostSaveDialog } from './PostSaveDialog';

type ContextValue = {
  onSavedSuccess: () => void;
};

const PostSavePromptContext = createContext<ContextValue | null>(null);

type Props = {
  navigateBack: () => void;
  title?: string;
  message?: string;
  children: React.ReactNode;
};

export const PostSavePromptProvider: React.FC<Props> = ({
  navigateBack,
  title,
  message,
  children,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.post_save_prompt',
  });

  const [open, setOpen] = useState(false);

  const onSavedSuccess = useCallback(() => setOpen(true), []);
  const onContinue = useCallback(() => setOpen(false), []);
  const onBackToList = useCallback(() => {
    setOpen(false);
    navigateBack();
  }, [navigateBack]);

  const value = useMemo(() => ({ onSavedSuccess }), [onSavedSuccess]);

  return (
    <PostSavePromptContext.Provider value={value}>
      {children}
      <PostSaveDialog
        open={open}
        title={title || t('title')}
        message={message || t('message')}
        continueLabel={t('continue_btn')}
        backToListLabel={t('back_to_list_btn')}
        onContinue={onContinue}
        onBackToList={onBackToList}
      />
    </PostSavePromptContext.Provider>
  );
};

export const usePostSavePrompt = () => {
  const ctx = useContext(PostSavePromptContext);
  if (!ctx) {
    throw new Error(
      'usePostSavePrompt must be used within PostSavePromptProvider',
    );
  }
  return ctx;
};

export const usePostSavePromptSafe = (): ContextValue => {
  const ctx = useContext(PostSavePromptContext);
  return useMemo(() => ctx ?? { onSavedSuccess: noop }, [ctx]);
};
